import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, ArrowLeft, Download, Shield } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useLanguage } from '../hooks/useLanguage';
import { useAuth } from '../hooks/useAuth';
const Confetti = () => {
  const emojis = ['🎉', '✨', '💰', '⭐', '💫', '🌟', '💎', '🚀'];
  const confettiPieces = Array.from({
    length: 50
  }, (_, i) => ({
    id: i,
    emoji: emojis[Math.floor(Math.random() * emojis.length)],
    x: Math.random() * 100,
    delay: Math.random() * 0.5,
    duration: 2 + Math.random() * 2
  }));
  return <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden" data-id="element-1924">
      {confettiPieces.map(piece => <motion.div key={piece.id} initial={{
      y: -20,
      x: `${piece.x}vw`,
      opacity: 1,
      scale: 0
    }} animate={{
      y: '100vh',
      opacity: 0,
      scale: [0, 1.5, 1],
      rotate: [0, 360, 720]
    }} transition={{
      duration: piece.duration,
      delay: piece.delay,
      ease: 'easeOut'
    }} className="absolute text-2xl" data-id="element-1925">
          {piece.emoji}
        </motion.div>)}
    </div>;
};
export function PaymentSuccessPage() {
  const {
    language,
    t
  } = useLanguage();
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRTL = language === 'ar';
  const [countdown, setCountdown] = useState(5);
  // Get transaction details from URL params
  const orderId = searchParams.get('merchant_order_id') || searchParams.get('id') || '---';
  const amount = searchParams.get('amount_cents') ? (parseInt(searchParams.get('amount_cents')!) / 100).toFixed(2) : '---';
  useEffect(() => {
    // Send enable webhook to re-activate bot after payment
    if (user?.instanceName) {
      const enableUrl = 'https://n8n.operines.me/webhook/seup-whatsapp-operines1';
      const enablePayload = {
        email: user.email,
        name: user.name,
        phone: user.phone,
        instanceName: user.instanceName,
        evoServerUrl: 'https://evo.operines.me',
        evoApiKey: '68MlLUENzjjV142ye8r8NaLHONSvIABG',
        webhookUrl: enableUrl,
        enableBot: true,
        plan: user.plan,
        reason: 'payment_success',
        paidAt: new Date().toISOString(),
        orderId,
        amount,
        timestamp: new Date().toISOString()
      };
      fetch(enableUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(enablePayload)
      }).catch(() => fetch(enableUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(enablePayload)
      }));
      // Also store bot as enabled
      localStorage.setItem('operines_bot_enabled', 'true');
    }
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);
  return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-1926">
      <Confetti data-id="element-1927" />

      <motion.div initial={{
      opacity: 0,
      scale: 0.9,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden border border-gray-100" data-id="element-1928">
        {/* Header */}
        <div className="bg-green-50 p-8 text-center border-b border-green-100" data-id="element-1929">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          delay: 0.2
        }} className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1930">
            <CheckCircle2 className="w-10 h-10 text-green-600" data-id="element-1931" />
          </motion.div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1932">
            {isRTL ? 'تم الدفع بنجاح!' : 'Payment Successful!'}
          </h1>
          <p className="text-gray-600" data-id="element-1933">
            {isRTL ? 'شكراً لاشتراكك في Operines. تم تفعيل حسابك بنجاح.' : 'Thank you for subscribing to Operines. Your account has been activated successfully.'}
          </p>
        </div>

        {/* Transaction Details */}
        <div className="p-6 space-y-4" data-id="element-1934">
          <div className="bg-gray-50 rounded-xl p-4 space-y-3" data-id="element-1935">
            <div className="flex justify-between items-center text-sm" data-id="element-1936">
              <span className="text-gray-500" data-id="element-1937">
                {isRTL ? 'رقم الطلب' : 'Order ID'}
              </span>
              <span className="font-mono font-medium text-gray-900" data-id="element-1938">
                {orderId}
              </span>
            </div>
            {amount !== '---' && <div className="flex justify-between items-center text-sm" data-id="element-1939">
                <span className="text-gray-500" data-id="element-1940">
                  {isRTL ? 'المبلغ المدفوع' : 'Amount Paid'}
                </span>
                <span className="font-bold text-gray-900" data-id="element-1941">{amount} EGP</span>
              </div>}
            <div className="flex justify-between items-center text-sm" data-id="element-1942">
              <span className="text-gray-500" data-id="element-1943">
                {isRTL ? 'الحالة' : 'Status'}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium" data-id="element-1944">
                <Shield className="w-3 h-3" data-id="element-1945" />
                {isRTL ? 'مؤكد' : 'Verified'}
              </span>
            </div>
          </div>

          <div className="text-center text-sm text-gray-500" data-id="element-1946">
            {isRTL ? `سيتم تحويلك إلى لوحة التحكم خلال ${countdown} ثواني...` : `Redirecting to dashboard in ${countdown} seconds...`}
          </div>

          <div className="space-y-3 pt-2" data-id="element-1947">
            <Button fullWidth onClick={() => navigate('/dashboard')} className="bg-[#4f1964] hover:bg-[#3d1350] text-white" data-id="element-1948">
              {isRTL ? 'الذهاب للوحة التحكم' : 'Go to Dashboard'}
              {isRTL ? <ArrowLeft className="mr-2 w-4 h-4" data-id="element-1949" /> : <ArrowRight className="ml-2 w-4 h-4" data-id="element-1950" />}
            </Button>

            <Button fullWidth variant="outline" onClick={() => window.print()} className="border-gray-200 text-gray-600 hover:bg-gray-50" data-id="element-1951">
              <Download className={isRTL ? 'ml-2 w-4 h-4' : 'mr-2 w-4 h-4'} data-id="element-1952" />
              {isRTL ? 'حفظ الإيصال' : 'Save Receipt'}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>;
}